import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSiteWallCameras } from "./useSiteWallCameras";
import {
  SiteVideoWallFullDialog,
  SiteVideoWallFullMode,
} from "./SiteVideoWallFullDialog";
import { getFocusGridPageForCamera } from "./siteVideoWallUtils";
import { WALL_GRID_PROTOCOL } from "./siteVideoWallConstants";
import { WallStreamsProvider } from "./WallStreams";

/**
 * Standalone full-wall page for opening the site video wall in a new tab
 * (e.g. via right-click → Open link in new tab on "Expand to full wall").
 */
export const SiteVideoWallPage: React.FC = () => {
  const navigate = useNavigate();
  const { siteId: siteIdParam } = useParams<{ siteId: string }>();
  const [searchParams] = useSearchParams();

  const siteId = siteIdParam ? Number(siteIdParam) : null;
  const siteName = searchParams.get("siteName") ?? "";
  const defaultFocusedCameraId = searchParams.get("cameraId");
  const detectionType = searchParams.get("detectionType") ?? "Alert";
  const cameraName = searchParams.get("cameraName") ?? "";
  const formattedDate = searchParams.get("date") ?? "";

  const [fullPage, setFullPage] = useState(0);
  const [fullMode, setFullMode] = useState<SiteVideoWallFullMode>("focus");
  const [focusedCameraId, setFocusedCameraId] = useState<string | null>(
    defaultFocusedCameraId
  );
  const focusGridPageSyncedRef = useRef(false);

  const { status, cameras, totalCount, ensureCamerasLoaded } =
    useSiteWallCameras(siteId, true);

  useEffect(() => {
    setFocusedCameraId(defaultFocusedCameraId);
    focusGridPageSyncedRef.current = false;
  }, [siteId, defaultFocusedCameraId]);

  useEffect(() => {
    if (fullMode !== "focus" || status !== "ready" || !focusedCameraId) {
      return;
    }
    if (focusGridPageSyncedRef.current) return;

    const pageForCamera = getFocusGridPageForCamera(focusedCameraId, cameras);
    if (pageForCamera !== null) {
      setFullPage(pageForCamera);
      focusGridPageSyncedRef.current = true;
      return;
    }

    if (cameras.length < totalCount) {
      void ensureCamerasLoaded(totalCount - 1);
    }
  }, [
    fullMode,
    status,
    focusedCameraId,
    cameras,
    totalCount,
    ensureCamerasLoaded,
  ]);

  const handleClose = useCallback(() => {
    navigate("/alerts");
  }, [navigate]);

  const handleModeChange = useCallback((mode: SiteVideoWallFullMode) => {
    setFullMode(mode);
    if (mode === "focus") {
      focusGridPageSyncedRef.current = false;
    } else {
      setFullPage(0);
    }
  }, []);

  const alertContextLabel = `${detectionType} · ${cameraName} · ${formattedDate}`;

  if (!siteId || Number.isNaN(siteId)) {
    return (
      <div className="flex h-screen items-center justify-center font-mulish text-gray-500">
        Invalid site for video wall.
      </div>
    );
  }

  return (
    <WallStreamsProvider
      protocol={WALL_GRID_PROTOCOL}
      lowLatency={false}
      enabled
    >
      <SiteVideoWallFullDialog
        open
        onClose={handleClose}
        siteName={siteName || `Site ${siteId}`}
        alertContextLabel={alertContextLabel}
        status={status}
        cameras={cameras}
        totalCount={totalCount}
        mode={fullMode}
        onModeChange={handleModeChange}
        page={fullPage}
        onPageChange={setFullPage}
        focusedCameraId={focusedCameraId}
        onFocusedCameraChange={setFocusedCameraId}
        ensureCamerasLoaded={ensureCamerasLoaded}
        closeLabel="Back to Alerts"
      />
    </WallStreamsProvider>
  );
};
